/**
 * Пересылка сообщений (договор: _vendor/chatwoot/_giper/FORWARD_MESSAGES.md): что можно переслать,
 * тело запроса, текст итога, пометка «Переслано из переписки с …». Чистые функции — проверяются без экрана
 * (forward.test.ts). Сам запрос — api/forward.ts, окно выбора получателя — components/chat/ForwardSheet.tsx.
 */
import { MESSAGE_INCOMING, MESSAGE_OUTGOING, type CwMessage, type InboxConversation, type UiMessage } from '@/api/inboxTypes';
import type { ChannelCapabilities } from '@/lib/channelRules';

/** Предел сообщений за раз (сервер отвечает 422 на большее). */
export const MAX_FORWARD = 50;

/** Куда пересылаем. */
export type ForwardTarget =
  | { conversation_id: number }
  | { inbox_id: number; query: string }
  | { inbox_id: number; contact_id: number };

export interface ForwardRequest {
  conversation_id: number;
  message_ids: number[];
  target: ForwardTarget;
  comment?: string;
  hide_author?: boolean;
}

export interface ForwardSkipped {
  message_id: number;
  file?: string | null;
  reason?: string | null;
}

export interface ForwardResult {
  conversation_id: number;
  inbox_id: number;
  forwarded: number;
  native: boolean;
  skipped: ForwardSkipped[];
}

/**
 * Можно ли переслать сообщение: только настоящие сообщения клиенту и от клиента.
 * Нет — заметки, служебные строки, шаблоны, ещё не доставленные (локальное эхо, id ≤ 0) и не отправившиеся.
 */
export function canForward(m: UiMessage): boolean {
  if (m.local || m.id <= 0) return false;
  if (m.private) return false;
  if (m.message_type !== MESSAGE_INCOMING && m.message_type !== MESSAGE_OUTGOING) return false;
  if (m.status === 'failed') return false;
  // Заметка бота «по вызову» помечена private, но на случай иного вида — бот-предложения не пересылаем.
  if (m.content_attributes?.unia_bot_invocation_id) return false;
  return true;
}

/** Номера отмеченных сообщений в порядке ленты (по времени), без непересылаемых, не больше предела. */
export function orderedSelection(messages: UiMessage[], selected: ReadonlySet<number>): number[] {
  return messages.filter((m) => selected.has(m.id) && canForward(m)).map((m) => m.id).slice(0, MAX_FORWARD);
}

/** Как изменится набор отметок при нажатии. Предел — MAX_FORWARD; limited = true, если упёрлись. */
export function toggleSelection(cur: ReadonlySet<number>, id: number): { next: Set<number>; limited: boolean } {
  const next = new Set(cur);
  if (next.has(id)) { next.delete(id); return { next, limited: false }; }
  if (next.size >= MAX_FORWARD) return { next, limited: true };
  next.add(id);
  return { next, limited: false };
}

/** Тело запроса. Пустая подпись не уходит; «скрыть отправителя» — только когда включено. */
export function buildForwardRequest(input: {
  convId: number;
  messageIds: number[];
  target: ForwardTarget;
  comment?: string;
  hideAuthor?: boolean;
}): ForwardRequest {
  const comment = (input.comment ?? '').trim();
  return {
    conversation_id: input.convId,
    message_ids: input.messageIds,
    target: input.target,
    ...(comment ? { comment } : {}),
    ...(input.hideAuthor ? { hide_author: true } : {}),
  };
}

/** Нужен ли переключатель «Скрыть отправителя»: цель в том же канале и канал умеет настоящую пересылку. */
export function canHideAuthor(
  sourceInboxId: number | null | undefined,
  targetInboxId: number | null | undefined,
  caps: Pick<ChannelCapabilities, 'native_forward'> | null | undefined,
): boolean {
  return !!sourceInboxId && sourceInboxId === targetInboxId && caps?.native_forward === true;
}

/** Каналы, где можно начать новый чат. */
export function newChatInboxes<T extends { id: number }>(inboxes: T[], caps: Map<number, ChannelCapabilities | null | undefined>): T[] {
  return inboxes.filter((i) => caps.get(i.id)?.new_chat === true);
}

/** Подсказка в поле «кому» для нового чата. */
export const NEW_CHAT_PLACEHOLDER = 'Ник, ссылка, телефон или почта';

/** Переписки под поисковую строку (по имени, телефону, почте) — для списка недавних, пока сервер ещё не ответил. */
export function filterRecipients(list: InboxConversation[], text: string, exclude?: { accountId: number; id: number }): InboxConversation[] {
  const q = text.trim().toLowerCase();
  return list.filter((c) => {
    if (exclude && c.accountId === exclude.accountId && c.id === exclude.id) return false;
    if (!q) return true;
    return [c.contact.name, c.contact.phone, c.contact.email].some((v) => (v ?? '').toLowerCase().includes(q));
  });
}

/** Склонение: 1 сообщение, 2 сообщения, 5 сообщений. */
export function pluralMessages(n: number): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return `${n} сообщений`;
  if (b === 1) return `${n} сообщение`;
  if (b >= 2 && b <= 4) return `${n} сообщения`;
  return `${n} сообщений`;
}

/** Строка про одно не ушедшее: «файл «a.pdf» — канал не принимает документы». */
function skippedLine(s: ForwardSkipped): string {
  const what = s.file ? `файл «${s.file}»` : `сообщение ${s.message_id}`;
  return s.reason ? `${what} — ${s.reason}` : what;
}

/** Итог для уведомления: «Переслано: 3», а если часть не ушла — словами, что и почему. */
export function forwardSummary(r: Pick<ForwardResult, 'forwarded' | 'skipped'>): string {
  const head = `Переслано: ${r.forwarded}`;
  if (!r.skipped?.length) return head;
  return `${head}. Не ушло: ${r.skipped.map(skippedLine).join('; ')}`;
}

/** Сколько показывать уведомление: с перечнем не ушедшего — дольше. */
export function summaryDuration(r: Pick<ForwardResult, 'skipped'>): number {
  return r.skipped?.length ? 8000 : 4500;
}

/** Пометка над пузырём: «Переслано из переписки с Иваном». null — сообщение не пересланное. */
export function forwardedNote(m: Pick<CwMessage, 'content_attributes'>): string | null {
  const f = m.content_attributes?.unia_forwarded;
  if (!f || typeof f !== 'object') return null;
  const from = (f as { from_contact?: unknown }).from_contact;
  const name = typeof from === 'string' ? from.trim() : '';
  return name ? `Переслано из переписки с ${name}` : 'Переслано';
}
